class LinkedProductSwatches extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.addEventListener('change', this.onSwatchChange.bind(this));

    if (this.dataset.shell === 'true' && this.dataset.defaultUrl) {
      this.swapToProduct(this.dataset.defaultUrl, this.dataset.defaultHandle);
    }
  }

  onSwatchChange(event) {
    const input = event.target;
    if (!input.matches('.linked-product-swatches__style-input')) return;
    if (input.value === this.dataset.currentHandle) return;

    this.swapToProduct(input.dataset.productUrl, input.value);
  }

  swapToProduct(productUrl, handle) {
    const container = document.getElementById(`section-${this.sectionId}`);
    if (!container) return;

    // white-only/pack-3-only (see linked-product-swatches.liquid) are only ever set by Liquid
    // on the SHELL product's own render, since it's the one carrying the restriction tags —
    // Long/Short/Briefs don't have them. But <linked-product-swatches> lives inside `container`
    // (the section-rendering API's swapped div), not outside it, so every swap's
    // `container.remove()` below destroys this very element (`this`) along with the old
    // content, and `newContainer`'s fetched HTML contains a brand new, tag-less
    // <linked-product-swatches> for whichever product was just fetched. Nothing carries the
    // flags forward by default. Capture them here from `this` (always accurate: on the shell's
    // initial auto-swap `this` is the shell's own freshly-parsed element; on a later pill-click
    // swap `this` is whatever swap-carried element is currently live, already correct from the
    // previous swap) and re-apply them onto the new element below, before it replaces this one.
    const preservedWhiteOnly = this.dataset.whiteOnly;
    const preservedPack3Only = this.dataset.packThreeOnly;

    // Whether window 'load' had already fired when newContainer was inserted — see the
    // add-to-cart override below. Captured at insertion time, not at override time, because the
    // script-load wait in between can straddle 'load'.
    let insertedAfterLoad = false;

    this.classList.add('linked-product-swatches--loading');

    const url = `${productUrl}?section_id=${this.sectionId}`;

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newContainer = html.getElementById(`section-${this.sectionId}`);
        if (!newContainer) return;

        // Shopify's section-rendering API wraps the section in an outer
        // <section id="shopify-section-...">, with the <script src>/<link> tags for that
        // section (e.g. media-gallery.js) as its direct children, siblings of the inner
        // id="section-..." div this snippet actually swaps in. Those siblings are never part
        // of newContainer, so their scripts must be collected separately here.
        const outerSection = newContainer.closest('[id^="shopify-section-"]') || html.body;
        const siblingScripts = Array.from(outerSection.querySelectorAll(':scope > script[src]'));

        // The product name shown here is the Style-switcher group's shared marketing name
        // (e.g. "Leak-Resistant Underwear"), not any individual Style product's own title —
        // swapping in the new product's real <h1> text would make the heading flicker between
        // different names as shoppers click between styles. Capture the heading text that's
        // on the page right now and re-apply it after the swap so it never changes.
        const oldTitleEl = container.querySelector('.product__title');
        const preservedTitleText = oldTitleEl ? oldTitleEl.textContent : null;

        // Color/Size are independent per Style product, so swapping in a new product's markup
        // also swaps in that product's *default* variant selection, silently discarding whatever
        // Color/Size the shopper had picked. Capture the current selections here so they can be
        // re-applied to the new product's inputs (when it offers matching values) after the swap.
        const preservedOptionSelections = Array.from(
          container.querySelectorAll('variant-radios input:checked, variant-selects select')
        ).reduce((selections, el) => {
          const name = el.tagName === 'SELECT' ? el.name.replace(/^options\[(.*)\]$/, '$1') : el.name;
          selections[name] = el.value;
          return selections;
        }, {});

        // Swapping the DOM immediately shows the new heading/text before its images have
        // loaded, since text paints instantly but <img src> requests only start once the tag
        // is actually in the document. That produces a visible flash of the new product name
        // over blank space before its photo catches up. Preloading the new gallery's images
        // while they're still off-DOM lets the browser cache them first, so by the time the
        // swap happens the photo is ready to paint in the same frame as the text.
        const imagesToPreload = Array.from(newContainer.querySelectorAll('img[src]'));
        const preloads = imagesToPreload.map(
          (img) =>
            new Promise((resolve) => {
              const loader = new Image();
              loader.onload = resolve;
              loader.onerror = resolve;
              loader.src = img.src;
            })
        );

        return Promise.race([
          Promise.all(preloads),
          new Promise((resolve) => setTimeout(resolve, 1500)),
        ]).then(() => {
          // The gallery/description height often differs between Style products, so a plain
          // replaceWith() reflows the page and everything below the section visibly jumps.
          // Lock the parent to its current pre-swap height, then release it (with a short
          // transition) once the new content is in and measured, so the page settles smoothly
          // instead of snapping.
          const parent = container.parentElement;
          const lockedHeight = container.getBoundingClientRect().height;
          if (parent) {
            parent.classList.add('linked-product-swatches__height-lock');
            parent.style.minHeight = `${lockedHeight}px`;
          }

          // Correct the title text and the Size/Color selection state on newContainer BEFORE it's
          // inserted/faded in, not after. These were previously fixed in the post-fade .then()
          // below, which runs only once the 300ms cross-fade has fully completed — meaning the
          // fade itself showed the *wrong* title and the *wrong* Size (Shopify's own server
          // default, e.g. "S") for its whole duration, then silently snapped to the correct
          // values right as the fade finished. That read as a visible flicker/flip after the
          // fade rather than a clean transition. Setting .textContent/.checked/.value here (while
          // newContainer is still off-DOM, before any fade class is applied) means the content
          // that actually fades in is already correct from its very first visible frame. Only the
          // *visual* state is set here — dispatching the 'change' event that makes the theme's
          // VariantSelects/VariantRadios recompute price/gallery/availability still happens later
          // (see below, post-fade) since that requires the custom elements to be upgraded, which
          // only happens once newContainer is actually connected to the document.
          const newTitleElPreFade = newContainer.querySelector('.product__title');
          if (newTitleElPreFade && preservedTitleText !== null) {
            newTitleElPreFade.textContent = preservedTitleText;
          }

          // Re-apply the white-only/pack-3-only flags (captured from `this` above) onto the new
          // <linked-product-swatches> element that just arrived in newContainer's fetched HTML —
          // see the comment at the top of this method for why they don't carry over on their own.
          const newSwatchesEl = newContainer.matches('linked-product-swatches')
            ? newContainer
            : newContainer.querySelector('linked-product-swatches');
          if (newSwatchesEl) {
            if (preservedWhiteOnly) newSwatchesEl.dataset.whiteOnly = preservedWhiteOnly;
            if (preservedPack3Only) newSwatchesEl.dataset.packThreeOnly = preservedPack3Only;
          }
          Object.entries(preservedOptionSelections).forEach(([optionName, value]) => {
            const radioMatch = newContainer.querySelector(
              `variant-radios input[name="${optionName}"][value="${CSS.escape(value)}"]`
            );
            if (radioMatch) {
              radioMatch.checked = true;
              return;
            }
            const selectMatch = newContainer.querySelector(`variant-selects select[name="options[${optionName}]"]`);
            if (selectMatch && Array.from(selectMatch.options).some((opt) => opt.value === value)) {
              selectMatch.value = value;
            }
          });
          if (!('Size' in preservedOptionSelections)) {
            const sizeRadioLPreFade = newContainer.querySelector('variant-radios input[name="Size" i][value="L"]');
            if (sizeRadioLPreFade && !sizeRadioLPreFade.disabled) {
              sizeRadioLPreFade.checked = true;
            } else if (!sizeRadioLPreFade) {
              const sizeSelectPreFade = newContainer.querySelector('variant-selects select[name="options[Size]" i]');
              const hasLPreFade =
                sizeSelectPreFade && Array.from(sizeSelectPreFade.options).some((opt) => opt.value === 'L');
              if (hasLPreFade) {
                sizeSelectPreFade.value = 'L';
              }
            }
          }

          // white-only flag (see linked-product-swatches.liquid): this shell's group members
          // still sell every Color elsewhere, so filtering happens here per-swap rather than in
          // Shopify admin. Force Color to White (overriding any preservedOptionSelections value,
          // since a shopper can't have legitimately picked a non-White color while inside this
          // white-only context) and hide every other Color swatch so it can't be selected again.
          // Done pre-fade, same as the title/Size=L correction above, so the fade never shows a
          // non-White selection or a full color row.
          if (this.dataset.whiteOnly === 'true') {
            const colorRadiosPreFade = Array.from(
              newContainer.querySelectorAll('variant-radios input[name="Color" i]')
            );
            if (colorRadiosPreFade.length > 0) {
              colorRadiosPreFade.forEach((radio) => {
                const isWhite = radio.value.toLowerCase() === 'white';
                radio.checked = isWhite;
                const label = newContainer.querySelector(`label[for="${radio.id}"]`) || radio.closest('label');
                if (!isWhite && label) label.style.display = 'none';
                if (!isWhite) radio.style.display = 'none';
              });
            } else {
              const colorSelectPreFade = newContainer.querySelector('variant-selects select[name="options[Color]" i]');
              if (colorSelectPreFade) {
                Array.from(colorSelectPreFade.options).forEach((opt) => {
                  const isWhite = opt.value.toLowerCase() === 'white';
                  if (isWhite) colorSelectPreFade.value = opt.value;
                  opt.hidden = !isWhite;
                  opt.disabled = !isWhite;
                });
              }
            }

            // main-product.liquid renders a separate #data-variant-name span next to the Color
            // fieldset, server-side, always showing the OPTION'S FIRST value (`option.values
            // limit:1`) regardless of which radio/select is actually checked. It's dead text,
            // not wired to the picker at all, so forcing the radio/select above never touches it
            // — left alone it keeps showing whatever color happens to be first in this product's
            // own option list (e.g. "Black") right next to a swatch that's now visually White.
            const variantNameEl = newContainer.querySelector('[data-variant-name]');
            if (variantNameEl) variantNameEl.textContent = 'White';

            // The gallery's featured/first-shown slide is server-rendered from
            // product.selected_or_first_available_variant (main-product.liquid), which is
            // whatever variant Shopify considers default for this Style — typically Black, not
            // White. Forcing the Color radio/select above doesn't touch the gallery at all (that
            // normally only happens via the theme's native 'change'-driven updateMedia(), which
            // runs post-fade below), so left alone the swap would show Black's photo for the
            // entire fade before flipping to White right at the end — the same
            // wrong-content-during-fade problem the title/Size=L fixes above already solved for
            // other fields. Reuse the same data-color-media map that color-image-fallback.js
            // uses (Color value -> media id) to find White's slide inside newContainer directly
            // and move it to the front / mark it active BEFORE insertion, exactly like
            // media-gallery.js's own setActiveMedia(id, true) does, but done by hand here since
            // newContainer's <media-gallery> custom element isn't upgraded yet (it's still
            // off-DOM at this point).
            try {
              // Read from newSwatchesEl (the freshly fetched product's own color-media map), not
              // `this` — `this` still carries the PREVIOUS product's media IDs, which don't
              // correspond to any slide inside newContainer's gallery.
              const colorMedia = JSON.parse((newSwatchesEl && newSwatchesEl.dataset.colorMedia) || '{}');
              const whiteMediaId = colorMedia['White'];
              const gallery = newContainer.querySelector('media-gallery');
              const galleryId = gallery && gallery.id.replace(/^MediaGallery-/, '');
              if (whiteMediaId && galleryId) {
                const slideId = `${galleryId}-${whiteMediaId}`;
                const whiteSlide = newContainer.querySelector(`[data-media-id="${slideId}"]`);
                if (whiteSlide) {
                  newContainer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
                  whiteSlide.classList.add('is-active');
                  whiteSlide.parentElement.prepend(whiteSlide);
                  const whiteThumbnail = newContainer.querySelector(`[data-target="${slideId}"]`);
                  if (whiteThumbnail) whiteThumbnail.parentElement.prepend(whiteThumbnail);
                }
              }

              // Hide every non-White product photo, not just each color's one featured_image —
              // most colors have several (front, back, lifestyle), and Shopify's storefront
              // Liquid can only see ONE photo per variant (variant.featured_media) — it has no
              // access to the full image->variant_ids list the Admin API exposes, so a
              // Liquid-side computation (tried first) could only ever find each color's single
              // featured photo, never its back/detail shots. Reading alt text directly in JS from
              // the actual thumbnail <img> tags instead sees every photo, not just the
              // Liquid-visible ones. Scoped specifically to the thumbnail rail
              // (.thumbnail-list.slider) per-project convention: every thumbnail whose alt text
              // does NOT contain "product-white" (the manual marker on confirmed White photos,
              // added once per product in Shopify admin) is treated as a non-White photo and
              // hidden, along with its corresponding main-gallery slide (same id, found via
              // data-target -> data-media-id). Hidden via CSS rather than removed from the DOM,
              // consistent with how the Color swatches and pack-1/pack-5 options are hidden
              // elsewhere in this feature.
              const thumbnailList = newContainer.querySelector('.thumbnail-list.slider');
              if (thumbnailList) {
                Array.from(thumbnailList.querySelectorAll('[data-target]')).forEach((thumbnail) => {
                  const alt = (thumbnail.querySelector('img')?.alt || '').toLowerCase();
                  if (alt.includes('product-white')) return;
                  thumbnail.style.display = 'none';
                  const targetId = thumbnail.dataset.target;
                  const matchingSlide = newContainer.querySelector(`[data-media-id="${targetId}"]`);
                  if (matchingSlide) matchingSlide.style.display = 'none';
                });
              }
            } catch (e) {
              // colorMedia JSON is best-effort; leave the gallery as fetched if anything's missing.
            }
          }

          // True cross-fade: stack the new content directly on top of the old (both occupying
          // the same box via the wrapper below) and fade opacity in both directions at once, so
          // there's never a blank instant between the old content disappearing and the new
          // content appearing — a sequential fade-out-then-fade-in leaves a brief blank gap that
          // reads as a flash. Images are already preloaded above, so the new layer paints
          // complete from the first frame it's visible.
          if (parent) {
            parent.classList.add('linked-product-swatches__crossfade-wrapper');
          }
          container.classList.add('linked-product-swatches__fade-out', 'linked-product-swatches__crossfade-layer');
          newContainer.classList.add('linked-product-swatches__fade-in', 'linked-product-swatches__crossfade-layer');
          insertedAfterLoad = document.readyState === 'complete';
          container.insertAdjacentElement('afterend', newContainer);

          const FADE_MS = 300;

          // Trigger both fades on the next frame so the browser commits the new layer's initial
          // opacity: 0 state first — adding the active class in the same tick would collapse the
          // transition and the content would just appear instantly.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              container.classList.add('linked-product-swatches__fade-out--active');
              newContainer.classList.add('linked-product-swatches__fade-in--active');
            });
          });

          return new Promise((resolve) => setTimeout(resolve, FADE_MS)).then(() => {
            container.remove();
            newContainer.classList.remove('linked-product-swatches__crossfade-layer', 'linked-product-swatches__fade-in');
            if (parent) parent.classList.remove('linked-product-swatches__crossfade-wrapper');
            return newContainer;
          });
        }).then((newContainer) => {
          const parent = newContainer.parentElement;

          // Release the height lock once the new content is in its final (non-absolute)
          // position and measured, so the surrounding page eases to the new height instead of
          // snapping.
          if (parent) {
            requestAnimationFrame(() => {
              parent.style.minHeight = `${newContainer.getBoundingClientRect().height}px`;
              setTimeout(() => {
                parent.style.minHeight = '';
                parent.classList.remove('linked-product-swatches__height-lock');
              }, 320);
            });
          }

          // Scripts parsed via DOMParser never execute when inserted through the DOM, so a
          // custom element defined by one of this section's own external scripts (e.g.
          // <media-gallery> from media-gallery.js) never registers unless that script already
          // ran once elsewhere on the page. That's normally true (recurring on every product
          // page), except on a "shell" landing page swapping in its very first product content,
          // where no matching element existed yet for the script to have upgraded. Detect any
          // custom element tag in the new content that isn't registered yet and re-run its
          // defining <script src> to force registration. This snippet's own script is skipped —
          // it's always already loaded by the time this code runs, and re-running it throws on
          // the redeclared class.
          const unregisteredTags = new Set(
            Array.from(newContainer.querySelectorAll('*'))
              .map((el) => el.tagName.toLowerCase())
              .filter((tag) => tag.includes('-') && tag !== 'linked-product-swatches' && !customElements.get(tag))
          );
          //
          // Dynamically inserted scripts load asynchronously, so the elements they define are
          // NOT upgraded yet when appendChild() returns. The 'change' dispatched below runs the
          // theme's VariantRadios.onVariantChange() -> updateMedia(), which calls
          // setActiveMedia() on <media-gallery>; firing it before media-gallery.js has executed
          // threw "setActiveMedia is not a function" on every shell page load (the Facebook ad
          // landing page, /products/leak-resistant-underwear) and left the gallery on the wrong
          // photo for the initial selection. So wait for every re-injected script to finish
          // loading (capped, so a failed/blocked script can't stall the swap) before continuing.
          const scriptLoads = [];
          if (unregisteredTags.size > 0) {
            siblingScripts.forEach((oldScript) => {
              if (oldScript.getAttribute('src').includes('linked-product-swatches.js')) return;

              const newScript = document.createElement('script');
              for (const { name, value } of oldScript.attributes) {
                newScript.setAttribute(name, value);
              }
              scriptLoads.push(
                new Promise((resolve) => {
                  newScript.onload = resolve;
                  newScript.onerror = resolve;
                })
              );
              document.body.appendChild(newScript);
            });
          }

          return Promise.race([
            Promise.all(scriptLoads),
            new Promise((resolve) => setTimeout(resolve, 3000)),
          ]).then(() => {
          this.dataset.currentHandle = handle;

          // Title text and Size/Color .checked/.value were already corrected on newContainer
          // before the fade-in started (see above) so the fade never shows the wrong content.
          // All that's left now that newContainer is connected to the document and its custom
          // elements are upgraded (via the re-registration just above) is to fire the one
          // 'change' event that makes VariantSelects/VariantRadios (theme.js) recompute
          // price/gallery/availability for whichever input ended up checked/selected — Color or
          // Size if the shopper had a prior selection to preserve, otherwise the Size=L default.
          // Firing only one 'change' (not one per option) avoids the double
          // renderProductInfo()/layout-jump bug — see the price/variant-picker-jump fixes above.
          const lastTouchedInput =
            newContainer.querySelector('variant-radios input:checked, variant-selects select') || null;
          if (lastTouchedInput) {
            lastTouchedInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // On page load, cart-drawer.js's ajaxCart.init() runs a one-time qtySelectors() pass
          // that finds every <input type="number">, and replaces each with the working .js-qty
          // widget built from the #JsQty Handlebars template — then binds the +/- buttons'
          // click handlers directly (jQuery .on('click', fn) on the matched set, not event
          // delegation), so that binding only ever covers widgets that existed at that one
          // moment and never reaches anything inserted afterward. qtySelectors() itself is
          // private to that module and isn't re-callable, and calling ajaxCart.init() again
          // would also re-run its add-to-cart form override with default settings, clobbering
          // the real ones from theme.liquid. So both the DOM upgrade and the click behavior are
          // replicated here instead, scoped only to the quantity input inside this freshly
          // swapped-in section, leaving the rest of ajaxCart's state untouched.
          const newQtyInput = newContainer.querySelector('input[type="number"][name="quantity"]');
          const jsQtyTemplateSrc = document.getElementById('JsQty');
          if (newQtyInput && jsQtyTemplateSrc && window.Handlebars) {
            const currentQty = parseInt(newQtyInput.value, 10) || 1;
            const template = window.Handlebars.compile(jsQtyTemplateSrc.innerHTML);
            const wrapper = document.createElement('div');
            wrapper.innerHTML = template({
              key: newQtyInput.dataset.id || '',
              itemQty: currentQty,
              itemAdd: currentQty + 1,
              itemMinus: currentQty - 1,
              inputName: newQtyInput.name,
              inputId: newQtyInput.id,
            });
            const jsQtyWidget = wrapper.firstElementChild;
            newQtyInput.after(jsQtyWidget);
            newQtyInput.remove();

            jsQtyWidget.querySelectorAll('.js-qty__adjust').forEach((button) => {
              button.addEventListener('click', () => {
                const numInput = jsQtyWidget.querySelector('.js-qty__num');
                let qty = parseInt(String(numInput.value).replace(/\D/g, ''), 10);
                if (!Number.isFinite(qty)) qty = 1;
                qty = button.classList.contains('js-qty__adjust--plus') ? qty + 1 : Math.max(1, qty - 1);
                numInput.value = qty;
              });
            });
          }
          document.dispatchEvent(new CustomEvent('linked-product-swatches:reinit-pack-deals'));

          // On page load, cart-drawer.js's ajaxCart.init() binds the add-to-cart <product-form>'s
          // submit handler once (jQuery .on('submit', fn) on the form matched at that moment), so
          // that binding is lost when the swap replaces the section with a brand new <product-form>
          // — its submit falls back to the browser's native, non-ajax behavior, which is why
          // "Add to Cart" was navigating to the full /cart page after a swap instead of opening the
          // side drawer. formOverride() itself is private to that module and isn't re-callable, and
          // calling ajaxCart.init() again would re-run qtySelectors() with default settings and
          // duplicate the upgrade already done above. So the override is replicated here instead,
          // scoped to this section's new form: intercept its submit, add the item via the same
          // public ShopifyAPI.addItemFromForm the original override uses, then call the public
          // ajaxCart.load() (equivalent to the real callback's cart refresh) to rebuild the drawer
          // and fire the 'afterCartLoad.ajaxCart' event that opens it. Error handling mirrors the
          // original's markup/placement (an .errors.qty-error div inserted before #CartContainer)
          // so any existing CSS for it keeps applying.
          //
          // On a "shell" landing page, this whole swap runs synchronously inside the
          // LinkedProductSwatches constructor, which fires during initial page parse — well before
          // window's 'load' event. cart-drawer.js's own ajaxCart.init() doesn't run until
          // $(window).on('load', ...) (see theme.liquid), so when it finally does run it queries
          // '.product-form' fresh and finds THIS ALREADY-SWAPPED-IN form, binding its own submit
          // handler to it. If this code had also bound one, both fire on every submit — one click
          // adds two /cart/add.js requests, corrupting quantities. So: only install this handler
          // when 'load' has already fired (meaning ajaxCart.init() already ran and missed this
          // form, e.g. a later swap from clicking a Style pill after the page finished loading).
          // On the initial shell auto-swap, skip installing anything here and let the theme's own
          // still-pending window.load handler bind normally, exactly as it would on any other page.
          const newForm = newContainer.querySelector('product-form form, form[data-type="add-to-cart-form"]');
          if (newForm && window.jQuery && window.ShopifyAPI && window.ajaxCart && insertedAfterLoad) {
            const $ = window.jQuery;
            const $newForm = $(newForm);
            const $addToCart = $('.AddToCart');
            const $cartContainer = $('#CartContainer');
            $newForm.on('submit', (evt) => {
              evt.preventDefault();
              $addToCart.removeClass('is-added').addClass('is-adding');
              $('.qty-error').remove();
              window.ShopifyAPI.addItemFromForm(
                evt.target,
                () => {
                  $addToCart.removeClass('is-adding').addClass('is-added');
                  window.ajaxCart.load();
                },
                (xhr) => {
                  $addToCart.removeClass('is-adding is-added');
                  let data;
                  try {
                    data = JSON.parse(xhr.responseText);
                  } catch (e) {
                    return;
                  }
                  if (data.message && data.status === 422) {
                    $cartContainer.before('<div class="errors qty-error">' + data.description + '</div>');
                  }
                }
              );
            });
          }

          // main-product.liquid initializes the PhotoSwipe zoom lightbox against the fixed,
          // page-static '#my-gallery' element via an inline <script type="module">. Inline
          // scripts are never re-run on swap (redeclaring their top-level bindings would throw),
          // so the lightbox is never re-bound to the freshly swapped-in gallery. Left uninitialized,
          // its own internal checkbox-based state toggle stays visible instead of hidden, which is
          // the stray checkbox that briefly flashes in the image's corner after a swap. Re-running
          // it here, scoped to this section only, keeps the lightbox working on the new content.
          if (newContainer.querySelector('#my-gallery')) {
            import('//cdn.shopify.com/s/files/1/0644/7526/7316/t/2/assets/product-zoom.js').then(
              ({ default: PhotoSwipeLightbox }) => {
                new PhotoSwipeLightbox({
                  gallery: '#my-gallery',
                  children: 'a',
                  pswpModule: () => import('//cdn.shopify.com/s/files/1/0644/7526/7316/t/2/assets/product-esm.js'),
                }).init();
              }
            );
          }

          const newTitle = html.querySelector('title');
          if (newTitle) document.title = newTitle.textContent;

          document.dispatchEvent(
            new CustomEvent('linked-product-swatches:swapped', {
              detail: { handle, url: productUrl },
            })
          );
          });
        });
      })
      .catch(() => {
        window.location.href = productUrl;
      })
      .finally(() => {
        this.classList.remove('linked-product-swatches--loading');
      });
  }
}

customElements.define('linked-product-swatches', LinkedProductSwatches);
